import React, { useMemo } from 'react';

import styled from '@emotion/styled';
import Print from '@mui/icons-material/Print';
import {
  Box, Button, FormControl, FormControlLabel, FormLabel, Paper, Radio, RadioGroup, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Theme, Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSeekaConverge } from '@seeka-labs/converge-react';

import { notPrintable } from '../../../shared/styles/notPrintable';
import { printAndScreen } from '../../../shared/styles/printAndScreen';
import { printOnly } from '../../../shared/styles/printOnly';

const useStyling = (theme: Theme) => ({
  bodyWeightTextField: {
    marginTop: "28px",
    background: "rgba(255,255,255,.15)",
    "& input": {
      color: "white",
      padding: "13px",
      width: "86px",
      marginTop: '2px',
      fontSize: "1.6rem",
    },
    "> div": {
      border: "1px solid white",
    },
    "> label": {
      fontSize: "1.6rem",
      color: "white!important",
      marginBottom: 2,
      left: "-13px",
      top: "-22px",
    },
    "& span": {
      color: "white!important",
    },
  },
  whiteButton: {
    color: "white",
    borderColor: 'white'
  },
  radioFormControl: {
    // background: "rgba(255,255,255,.15)",
    "& input": {
      color: "white",
      // padding: "13px",
      // maxWidth: "110px",
      // fontSize: "1.6rem",
    },
    // "> div": {
    //   border: "1px solid white",
    // },
    "> label": {
      fontSize: "18px",
      color: "white!important",
      // marginBottom: 2,
      // left: "-13px",
      // top: "-22px",
    },
    "& span": {
      color: "white!important",
    },
  },
})

type DosageTableProps = {
  bodyWeightUnit: "kg" | "lbs"
  bodyWeight: number | null
  dosageType: 'single' | 'split'
  mgPerKg: number
}

export const defaultMgPerKg = 0.2;

// const mgPerKgOptions = [defaultMgPerKg, 0.5, 1]
// const mgPerKgOptions = [defaultMgPerKg, 0.5]
const mgPerKgOptions = [defaultMgPerKg]

const DosageTable = ({ bodyWeightUnit, bodyWeight, dosageType, mgPerKg }: DosageTableProps) => {
  const theme = useTheme();

  const weightInKg = bodyWeightUnit === 'kg' ? bodyWeight : poundsToKilograms(bodyWeight)

  const standardDosageMg = (weightInKg || 0) * mgPerKg
  const standardDosageMilliliters = standardDosageMg / 10
  const mlToDrops = (ml: number) => ml * 15.5; // 0.0645ml = 1 drop, 15.5 drops = 1ml
  const quarterDose = 0.25;
  const halfDose = 0.5;
  const fullDose = 1;
  const threeQuarterDose = 0.75;
  // const doubleDose = 2;

  const dosageBottleLifeTime = useMemo(() => {
    const standardDosePerDay = standardDosageMilliliters * fullDose; // Full daily dosage in milliliters
    const totalDaysFor50ml = 50 / standardDosePerDay; // Days a 50ml bottle will last
    const totalDaysFor100ml = 100 / standardDosePerDay; // Days a 100ml bottle will last

    return {
      fiftyMilliliterBottleDays: totalDaysFor50ml / 7, // Convert to weeks
      hundredMilliliterBottleDays: totalDaysFor100ml / 7, // Convert to weeks
    };
  }, [bodyWeight, bodyWeightUnit, dosageType, mgPerKg]);

  const dosages = dosageType === 'single' ?
    [
      {
        name: <>Trial 1</>,
        dosageMilliliters: <>{(standardDosageMilliliters * quarterDose).toFixed(2)}ml</>,
        dosageMilligrams: <>{Math.round(standardDosageMg * quarterDose)}mg</>,
        dosageDrops: <>{Math.round(mlToDrops(standardDosageMilliliters * quarterDose))} drops</>,
        time: <>Morning</>,
        day: 'Day 1'
      },
      {
        name: <>Trial 2</>,
        dosageMilliliters: <>{(standardDosageMilliliters * halfDose).toFixed(2)}ml</>,
        dosageMilligrams: <>{Math.round(standardDosageMg * halfDose)}mg</>,
        dosageDrops: <>{Math.round(mlToDrops(standardDosageMilliliters * halfDose))} drops</>,
        time: <>Morning</>,
        day: 'Day 2'
      },
      {
        name: <>Trial 3</>,
        dosageMilliliters: <>{(standardDosageMilliliters * threeQuarterDose).toFixed(2)}ml</>,
        dosageMilligrams: <>{Math.round(standardDosageMg * threeQuarterDose)}mg</>,
        dosageDrops: <>{Math.round(mlToDrops(standardDosageMilliliters * threeQuarterDose))} drops</>,
        time: <>Morning</>,
        day: 'Day 3'
      },
      // {
      //   name: <>Loading dose</>,
      //   dosageMilliliters: <>{(standardDosageMilliliters * doubleDose).toFixed(2)}ml</>,
      //   dosageMilligrams: <>{Math.round(standardDosageMg * doubleDose)}mg</>,
      //   dosageDrops: <>{Math.round(mlToDrops(standardDosageMilliliters * doubleDose))} drops</>,
      //   time: <>Morning</>,
      //   day: 'Day 3 - 16'
      // },
      {
        name: <>Standard</>,
        dosageMilliliters: <>{(standardDosageMilliliters * fullDose).toFixed(2)}ml</>,
        dosageMilligrams: <>{Math.round(standardDosageMg * fullDose)}mg</>,
        dosageDrops: <>{Math.round(mlToDrops(standardDosageMilliliters * fullDose))} drops</>,
        time: <>Morning</>,
        day: 'Day 4+'
      }
    ]
    : [
      {
        name: <>Trial 1</>,
        dosageMilliliters: <>{(standardDosageMilliliters * (quarterDose / 2)).toFixed(2)}ml<br /><br />{(standardDosageMilliliters * (quarterDose / 2)).toFixed(2)}ml</>,
        dosageMilligrams: <>{Math.round(standardDosageMg * (quarterDose / 2))}mg<br /><br />{Math.round(standardDosageMg * (quarterDose / 2))}mg</>,
        dosageDrops: <>{Math.round(mlToDrops(standardDosageMilliliters * (quarterDose / 2)))} drops<br /><br />{Math.round(mlToDrops(standardDosageMilliliters * (quarterDose / 2)))} drops</>,
        time: <>Morning<br /><br />Afternoon</>,
        day: 'Day 1'
      },
      {
        name: <>Trial 2</>,
        dosageMilliliters: <>{(standardDosageMilliliters * (halfDose / 2)).toFixed(2)}ml<br /><br />{(standardDosageMilliliters * (halfDose / 2)).toFixed(2)}ml</>,
        dosageMilligrams: <>{Math.round(standardDosageMg * (halfDose / 2))}mg<br /><br />{Math.round(standardDosageMg * (halfDose / 2))}mg</>,
        dosageDrops: <>{Math.round(mlToDrops(standardDosageMilliliters * (halfDose / 2)))} drops<br /><br />{Math.round(mlToDrops(standardDosageMilliliters * (halfDose / 2)))} drops</>,
        time: <>Morning<br /><br />Afternoon</>,
        day: 'Day 2'
      },
      {
        name: <>Trial 3</>,
        dosageMilliliters: <>{(standardDosageMilliliters * (threeQuarterDose / 2)).toFixed(2)}ml<br /><br />{(standardDosageMilliliters * (threeQuarterDose / 2)).toFixed(2)}ml</>,
        dosageMilligrams: <>{Math.round(standardDosageMg * (threeQuarterDose / 2))}mg<br /><br />{Math.round(standardDosageMg * (threeQuarterDose / 2))}mg</>,
        dosageDrops: <>{Math.round(mlToDrops(standardDosageMilliliters * (threeQuarterDose / 2)))} drops<br /><br />{Math.round(mlToDrops(standardDosageMilliliters * (threeQuarterDose / 2)))} drops</>,
        time: <>Morning<br /><br />Afternoon</>,
        day: 'Day 3'
      },
      {
        name: <>Standard</>,
        dosageMilliliters: <>{(standardDosageMilliliters * (fullDose / 2)).toFixed(2)}ml<br /><br />{(standardDosageMilliliters * (fullDose / 2)).toFixed(2)}ml</>,
        dosageMilligrams: <>{Math.round(standardDosageMg * (fullDose / 2))}mg<br /><br />{Math.round(standardDosageMg * (fullDose / 2))}mg</>,
        dosageDrops: <>{Math.round(mlToDrops(standardDosageMilliliters * (fullDose / 2)))} drops<br /><br />{Math.round(mlToDrops(standardDosageMilliliters * (fullDose / 2)))} drops</>,
        time: <>Morning<br /><br />Afternoon</>,
        day: 'Day 4+'
      }
    ];

  const cellStyle = {
    borderColor: theme.palette.grey[400],
    padding: { xs: undefined, sm: undefined, md: 2 }
  }

  return (
    <Stack sx={{ width: '100%' }} gap={{ xs: 2 }}>
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table size={"small"} aria-label="dosage table">
          <TableBody>
            {dosages.map((row, i) => (
              <TableRow
                key={i}
                sx={{ '&:last-child td, &:last-child th': { border: 0 }, borderColor: 'red' }}
              >
                <TableCell component="th" scope="row"
                  sx={cellStyle}>
                  {row.day}
                </TableCell>
                <TableCell
                  sx={cellStyle}>{row.name}<br />{row.dosageMilligrams}</TableCell>
                {dosageType === 'split' && <TableCell component="th" scope="row"
                  sx={cellStyle}>
                  {row.time}
                </TableCell>}
                <TableCell align="right"
                  sx={cellStyle}>{row.dosageDrops}</TableCell>
                <TableCell align="right"
                  sx={cellStyle}>{row.dosageMilliliters}</TableCell>
                {/* <TableCell align="right"
                  sx={{ ...cellStyle, display: { xs: 'none', sm: 'table-cell' } }}>{row.dosageMilligrams}</TableCell> */}
                {/* <TableCell align="right"
                  sx={{ ...cellStyle }}>{row.dosageMilligrams}</TableCell> */}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack sx={{ mt: { xs: 1, md: 1 }, textAlign: 'center', visibility: !bodyWeight ? 'hidden' : 'initial' }} direction={'row'} gap={5} justifyContent={'center'}>
        <Typography variant="body2" gutterBottom color="inherit">
          50ml bottle will last {dosageBottleLifeTime.fiftyMilliliterBottleDays.toFixed(0)} {dosageBottleLifeTime.fiftyMilliliterBottleDays > 1 ? 'weeks' : 'week'}
        </Typography>
        <Typography variant="body2" gutterBottom color="inherit">
          100ml bottle will last {dosageBottleLifeTime.hundredMilliliterBottleDays.toFixed(0)} {dosageBottleLifeTime.hundredMilliliterBottleDays > 1 ? 'weeks' : 'week'}
        </Typography>
      </Stack>

      <Typography variant="body2" gutterBottom color="inherit" sx={{ mt: { xs: 1, md: 1 } }}>
        This calculator is for informational purposes only.
        Consult with your doctor before taking any supplements.
      </Typography>
    </Stack>
  )
}

const poundsToKilograms = (pounds: number) => {
  return pounds * 0.45359237;
}

export const DosageCalculator = () => {
  const theme = useTheme()
  const [bodyWeight, setBodyWeight] = React.useState<number | null>(80)
  const [mgPerKg, setMgPerKg] = React.useState<number>(defaultMgPerKg)
  const [bodyWeightUnit, setBodyWeightUnit] = React.useState<"kg" | "lbs">("kg")
  const [dosageType, setDosageType] = React.useState<'single' | 'split'>('single');

  const handleChangeDosageType = (event: React.ChangeEvent<HTMLInputElement>) => {
    const dType = (event.target as HTMLInputElement).value;
    setDosageType(dType as any);
    onInteraction('Dosage type changed', { dosageType: dType })
  };

  const handleChangeMgPerKg = (event: React.ChangeEvent<HTMLInputElement>) => {
    // console.log('event.target.value', (event.target as HTMLInputElement).value)
    setMgPerKg(parseFloat((event.target as HTMLInputElement).value));
    onInteraction('Dosage strength changed')
  };

  const converge = useSeekaConverge();

  const styles = useStyling(theme)

  const handleBodyWeightUnitChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const unit = (event.target as HTMLInputElement).value as "kg" | "lbs";
    setBodyWeightUnit(unit)

    if (unit === 'kg') {
      setBodyWeight(parseFloat(poundsToKilograms(bodyWeight).toFixed(1)))
    } else {
      setBodyWeight(parseFloat((bodyWeight * 2.20462262185).toFixed(1)))
    }
  }

  const weightUnitSelector = (
    <RadioGroup
      sx={{ color: "white", fontSize: "1.5rem", p: 0 }}
      row
      aria-labelledby="weight unit"
      name="weight-unit"
      value={bodyWeightUnit}
      onChange={handleBodyWeightUnitChange}
    >
      <FormControlLabel value="kg" control={<Radio />} label="kg" />
      <FormControlLabel value="lbs" control={<Radio />} label="lbs" />
    </RadioGroup>
  )

  const onInteraction = (interactionName: string, props?: { [key: string]: string; }) => {
    converge.track.custom('Calculator interaction', {
      contentName: interactionName,
      custom: props
    })
  }

  const onPrint = () => {
    onInteraction('Print')
    window.print();
  }

  const NotPrintable = useMemo(() => styled('div')(notPrintable), []);
  const PrintOnly = useMemo(() => styled('div')(printOnly), []);

  const TextColorOnPrint = useMemo(() => styled('div')(printAndScreen({
    color: 'white'
  },
    {
      color: 'black'
    }
  )), []);

  return (
    (<TextColorOnPrint>
      <Box
        borderRadius={theme.shape.borderRadius}
        sx={{
          p: { xs: 1, sm: 3, md: 4 },
          pt: { xs: 5, sm: 4, md: 4 },
          // background: `linear-gradient(180deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.dark} 100%)`,
        }}
      >
        <Stack gap={4}>
          <PrintOnly>
            <Typography variant="h2" color="inherit">
              Blootropic.com
            </Typography>
          </PrintOnly>

          <Typography variant="h3" color="inherit" sx={{ marginBottom: 2 }}>
            Dosage for 1% Methylene Blue Liquid (aqueous solution) - {mgPerKg}mg / kg
          </Typography>

          <Stack direction={{ xs: "column", sm: "column", md: "row" }} gap={4}>
            <PrintOnly>
              <Stack direction="column" gap={5}>
                <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                  <Table size={"small"} aria-label="dosage table">
                    <TableHead>
                      <TableRow>
                        <TableCell>For body weight</TableCell>
                        <TableCell align="right">Dosage strength</TableCell>
                        <TableCell align="right">Dosage type</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow
                        sx={{ '&:last-child td, &:last-child th': { border: 0 }, borderColor: 'red' }}
                      >
                        <TableCell component="th" scope="row">
                          {bodyWeight} {bodyWeightUnit}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {mgPerKg.toFixed(2)}mg / kg
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {dosageType}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            </PrintOnly>

            <NotPrintable>
              <Stack direction="column" rowGap={{ xs: 4, md: 4 }} alignItems={{ xs: 'center', sm: 'flex-start' }}>
                <Box sx={{ width: '270px' }}>
                  <TextField
                    label="Your body weight"
                    variant="outlined"
                    type="number"
                    id="body-weight"
                    value={bodyWeight}
                    slotProps={{
                      input: {
                        endAdornment: weightUnitSelector,
                      }
                    }}
                    onChange={(e) => {
                      const dirty = e.target.value
                      if (dirty === "" || dirty === undefined) {
                        setBodyWeight(null)
                      } else {
                        setBodyWeight(parseFloat(dirty))
                      }
                      onInteraction('Body weight changed')
                    }}
                    sx={styles.bodyWeightTextField}
                  />
                </Box>

                <Box sx={[mgPerKgOptions.length > 1 ? {
                  display: null
                } : {
                  display: 'none'
                }]}>
                  <FormControl sx={styles.radioFormControl}>
                    <FormLabel id="dosage-strength" sx={{ textAlign: 'left' }}>Strength - mg/kg</FormLabel>
                    <RadioGroup
                      row
                      value={mgPerKg}
                      onChange={handleChangeMgPerKg}
                      aria-labelledby="dosage-strength"
                      name="dosage-strength-group"
                      id="dosage-strength-group"
                    >
                      {mgPerKgOptions.map((option) => (
                        <FormControlLabel key={`mgoption-${option}`} id={`mgoption-${option}`} value={option} control={<Radio />} label={`${option.toFixed(2)}`} />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </Box>

                <Box>
                  <FormControl sx={styles.radioFormControl}>
                    <FormLabel id="dosage-type" sx={{ textAlign: 'left' }}>Dosage type</FormLabel>
                    <RadioGroup
                      row
                      value={dosageType}
                      onChange={handleChangeDosageType}
                      aria-labelledby="dosage-type"
                      name="dosage-type-group"
                    >
                      <FormControlLabel value="single" control={<Radio />} label="Single" />
                      <FormControlLabel value="split" control={<Radio />} label="Split" />
                    </RadioGroup>
                  </FormControl>
                </Box>

                <Stack direction={'row'} justifyContent={'space-between'} sx={{ mt: { xs: 0, md: 0 } }}>
                  <Button variant="outlined" onClick={onPrint} sx={styles.whiteButton} startIcon={<Print />}>
                    Print
                  </Button>
                  {/* <Button variant="outlined" color="primary" sx={styles.whiteButton} startIcon={<Email />}>
                  Email
                </Button> */}
                </Stack>

              </Stack>
            </NotPrintable>

            <DosageTable bodyWeight={bodyWeight} bodyWeightUnit={bodyWeightUnit} dosageType={dosageType} mgPerKg={mgPerKg} />
          </Stack>
        </Stack>
      </Box>
    </TextColorOnPrint>)
  );
}
